import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";


export const danaUploadErrorMessages = {
    [UploadError.NotEnough]: siteMessage("QuickOrder_NotEnoughQuantity"),
    [UploadError.ConfigurableProduct]: siteMessage("QuickOrder_CannotOrderConfigurable"),
    [UploadError.StyledProduct]: siteMessage("QuickOrder_CannotOrderStyled"),
    [UploadError.Unavailable]: siteMessage("Dana_WishList_PartUpload_NoRightsToBuy"),
    [UploadError.NotFound]: siteMessage("Product_NotFound"),
    [UploadError.InvalidUnit]: translate("Invalid U/M"),
    [UploadError.OutOfStock]: siteMessage("Inventory_OutofStock"),
    [UploadError.WrongType]: siteMessage("Dana_WishList_PartUpload_InvalidDateFormat"),
} as any
export type WizardStep = 1 | 2 | 3;
export type ImportTab = "paste" | "upload";
export type RowStatus = "valid" | "invalid" | "rtb";

export interface InputItem {
    name: string;
    qtyOrdered?: number;
    requestedShipDate?: string;
}

export interface EditableRow {
    id: string;
    rowNumber: number;
    partNumber: string;
    partName: string;
    qty: number;
    requestedShipDate?: string;
    hasDateError?: boolean;
    hasQtyError?: boolean;
    replacementPart?: ProductDto;
    replacementPartNumber?: string;
    status: RowStatus;
    errorMessage?: string;
    needsReview?: boolean;
    showSuggestions: boolean;
    deleted: boolean;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
}

export function checkQtyError(qty: number, minimumOrderQty?: number, multipleSaleQty?: number): boolean {
    const minQty = (minimumOrderQty ?? 0) > 0 ? minimumOrderQty! : 1;
    const packQty = (multipleSaleQty ?? 0) > 1 ? multipleSaleQty! : 1;
    return qty < minQty || (packQty > 1 && qty % packQty !== 0);
}

export function checkDateError(requestedShipDate: string | undefined, isEmea: boolean): boolean {
    if (requestedShipDate === undefined) return false;
    if (requestedShipDate === "") return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isEmea) {
        const ddmmyyyyRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (ddmmyyyyRegex.test(requestedShipDate)) {
            const [day, month, year] = requestedShipDate.split("/");
            const productDate = new Date(Number(year), Number(month) - 1, Number(day));
            if (Number.isFinite(productDate.getTime()) && productDate >= today) {
                return false;
            }
        }
    } else {
        const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
        if (mmddyyyyRegex.test(requestedShipDate)) {
            const [month, day, year] = requestedShipDate.split("/");
            const productDate = new Date(Number(year), Number(month) - 1, Number(day));
            if (Number.isFinite(productDate.getTime()) && productDate >= today) {
                return false;
            }
        }
    }

    return true;
}
export interface DanaOrderUploadFileUploadStyles {
    mainWrapper?: InjectableCss;
    uploadLinkTooltipText?: TypographyPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileUploader?: FileUploadPresentationProps;
    includeFirstRowCheckbox?: CheckboxProps;
    cancelUploadButton?: ButtonPresentationProps;
    uploadFileButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    uploadingLabel?: TypographyPresentationProps;
    uploadFileLabel?: TypographyPresentationProps;
    backButton?: ButtonPresentationProps;
    nextButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
}

export function parsePastedText(raw: string): InputItem[] {
    return raw
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
            const parts = line.split(/[\t,|]/);
            const name = parts[0]?.trim();
            if (!name) return null;
            const field1 = parts[1]?.trim();
            const field2 = parts[2]?.trim();
            const datePattern = /^\d{1,2}[\/\-\|\t]\d{1,2}[\/\-\|\t]\d{4}$/;
            const normalizeDate = (s: string) => s.replace(/[-|\t]/g, "/");
            const field1IsDate = !!field1 && datePattern.test(field1);
            const qtyRaw = field1IsDate && !field2 ? undefined : field1;
            const shipDate = field1IsDate && !field2 ? field1 : field2;
            return {
                name,
                qtyOrdered: qtyRaw ? parseFloat(qtyRaw) || undefined : undefined,
                requestedShipDate: shipDate ? normalizeDate(shipDate) : undefined,
            } as InputItem;
        })
        .filter((item): item is InputItem => item !== null && item.name.length > 0);
}
